import * as yup from 'yup';

import { Button, Col, Form, FormGroup, Label, Row } from 'reactstrap';

import { CredentialingExamProgramResult } from '../common/Types';
import { Input } from '.';
import React, { useCallback, useMemo } from 'react';
import { programService } from '../services/ProgramService';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

const FlexFormGroup = styled(FormGroup)`
    display: flex;
    justify-content: start;
    align-items: center;
    & label {
        flex-basis: calc(75% - 10px);
    }
    & div,
    & span {
        flex-basis: calc(25% - 10px);
    }
`;

interface ExamFormProps {
    result: CredentialingExamProgramResult;
    totalGraduates: number;
    onResultUpdated: (outcome: CredentialingExamProgramResult) => void;
    onCancel: () => void;
}

interface ExamFormData {
    id: number;
    outcomeTypeId: number;
    programId: number;
    graduatingYear: number;
    numberOfGradsAttemptingExam: number;
    passing1stAttempt: number;
    passingSubsequentAttempts: number;
    reportYear: number;
    total: number;
}

const ExamForm = (props: ExamFormProps) => {
    const { result, totalGraduates, onResultUpdated, onCancel } = props;

    // Memoize validation schema
    const validationSchema = useMemo(() => {
        return yup.object({
            id: yup.number().required().default(0),
            outcomeTypeId: yup.number().required(),
            programId: yup.number().required(),
            graduatingYear: yup.number().required(),
            numberOfGradsAttemptingExam: yup
                .number()
                .required('Number of graduates attempting exam is required')
                .min(0, 'Must be 0 or more')
                .max(totalGraduates, `Must be at or less than # Graduates ${totalGraduates}`)
                .typeError('Must be a valid number'),
            passing1stAttempt: yup
                .number()
                .required('Number passing 1st attempt is required')
                .min(0, 'Must be 0 or more')
                .typeError('Must be a valid number')
                .test('first-attempt-validation', 'Cannot exceed attempting exam count', function (value) {
                    const { numberOfGradsAttemptingExam } = this.parent;
                    if (value > numberOfGradsAttemptingExam) {
                        return this.createError({
                            message: `Cannot exceed ${numberOfGradsAttemptingExam} attempting exam`,
                        });
                    }
                    return true;
                }),
            passingSubsequentAttempts: yup
                .number()
                .required('Number passing subsequent attempts is required')
                .min(0, 'Must be 0 or more')
                .typeError('Must be a valid number'),
            reportYear: yup.number().required(),
            total: yup
                .number()
                .test(
                    'total-validation',
                    'Total passing graduates cannot exceed number attempting exam',
                    function (value) {
                        const { numberOfGradsAttemptingExam, passing1stAttempt, passingSubsequentAttempts } =
                            this.parent;
                        const total = (passing1stAttempt || 0) + (passingSubsequentAttempts || 0);
                        if (total > (numberOfGradsAttemptingExam || 0)) {
                            return this.createError({
                                message: `Total passing graduates (${total}) cannot exceed number attempting exam (${numberOfGradsAttemptingExam})`,
                            });
                        }
                        return true;
                    },
                ),
        });
    }, [totalGraduates]);

    // Memoize default values
    const defaultValues = useMemo(
        (): ExamFormData => ({
            id: result.id || 0,
            outcomeTypeId: result.outcomeTypeId || 0,
            programId: result.programId || 0,
            graduatingYear: result.graduatingYear || 0,
            numberOfGradsAttemptingExam: result.numberOfGradsAttemptingExam || 0,
            passing1stAttempt: result.passing1stAttempt || 0,
            passingSubsequentAttempts: result.passingSubsequentAttempts || 0,
            reportYear: result.reportYear || 0,
            total: (result.passing1stAttempt || 0) + (result.passingSubsequentAttempts || 0),
        }),
        [result],
    );

    const {
        control,
        handleSubmit,
        formState: { errors, isDirty, isSubmitting },
    } = useForm<ExamFormData>({
        resolver: yupResolver(validationSchema) as any,
        mode: 'onChange',
        defaultValues,
    });

    const onSubmit = useCallback(
        async (values: ExamFormData) => {
            const toastId = toast.info('Saving exam results...');

            try {
                const examResult: CredentialingExamProgramResult = {
                    ...values,
                    total: (values.passing1stAttempt || 0) + (values.passingSubsequentAttempts || 0),
                };

                const result = await programService.saveProgramCredentialingExams(examResult);

                toast.update(toastId, {
                    type: 'success',
                    render: 'Exam results saved',
                });

                onResultUpdated(result);
            } catch (error) {
                toast.update(toastId, {
                    type: 'error',
                    render: 'Error saving exam results',
                });
            }
        },
        [onResultUpdated],
    );

    return (
        <Form noValidate onSubmit={handleSubmit(onSubmit)}>
            <Row>
                <Col className={'d-flex flex-column justify-content-between'}>
                    <FlexFormGroup>
                        <Label>{`# Graduates`}</Label>
                        <span>{totalGraduates}</span>
                    </FlexFormGroup>
                    <FlexFormGroup>
                        <Label>{`# Graduates Attempted`}</Label>
                        <div>
                            <Controller
                                name="numberOfGradsAttemptingExam"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        type="number"
                                        {...field}
                                        value={field.value || ''}
                                        onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : 0)}
                                    />
                                )}
                            />
                            {errors?.numberOfGradsAttemptingExam?.message && (
                                <Label className="text-danger">{errors.numberOfGradsAttemptingExam.message}</Label>
                            )}
                        </div>
                    </FlexFormGroup>
                    <FlexFormGroup>
                        <Label>{`# Graduates Passed - 1st Attempt`}</Label>
                        <div>
                            <Controller
                                name="passing1stAttempt"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        type="number"
                                        {...field}
                                        value={field.value || ''}
                                        onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : 0)}
                                    />
                                )}
                            />
                            {errors?.passing1stAttempt?.message && (
                                <Label className="text-danger">{errors.passing1stAttempt.message}</Label>
                            )}
                        </div>
                    </FlexFormGroup>
                    <FlexFormGroup>
                        <Label>{`# Graduates Passed - Subsequent Attempts`}</Label>
                        <div>
                            <Controller
                                name="passingSubsequentAttempts"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        type="number"
                                        {...field}
                                        value={field.value || ''}
                                        onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : 0)}
                                    />
                                )}
                            />
                            {errors?.passingSubsequentAttempts?.message && (
                                <Label className="text-danger">{errors.passingSubsequentAttempts.message}</Label>
                            )}
                        </div>
                    </FlexFormGroup>
                </Col>
            </Row>
            {errors?.total?.message && (
                <Row className="mt-2">
                    <Col>
                        <Label className="text-danger">{errors.total.message}</Label>
                    </Col>
                </Row>
            )}
            <Row className="mt-2">
                <Col>
                    <Button disabled={isSubmitting} type="submit" color="primary">
                        {isSubmitting ? 'Saving...' : 'Submit'}
                    </Button>
                    <Button type="button" color="secondary" className="ml-3" onClick={onCancel}>
                        Cancel
                    </Button>
                </Col>
            </Row>
        </Form>
    );
};

export default ExamForm;
